import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  MapPin, Star, Users, ArrowRight, Phone, Mail, Calendar,
  ChefHat, Music, Camera, Sparkles, Truck, Wine, Check
} from 'lucide-react';
import SEO from '../components/SEO';
import { breadcrumbLD, organizationLD } from '../utils/structuredData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CITY_CONFIG = {
  denver: {
    key: 'denver', label: 'Denver', state: 'CO', full: 'Denver, Colorado',
    tagline: 'Mile High Events, World-Class Experiences',
    description: 'From rooftop cocktail parties with Rocky Mountain views to elegant ballroom galas, Eventism Denver brings premium event services to the Mile High City. Trusted by hundreds of Denver hosts.',
    hero_gradient: 'from-blue-900 via-indigo-900 to-slate-900',
    accent: 'indigo',
    highlights: ['Rocky Mountain Venue Access', 'Farm-to-Table Colorado Cuisine', 'Craft Brewery & Distillery Partners', 'Altitude-Aware Event Planning'],
    stats: { events: '500+', rating: '4.9', vendors: '40+' },
  },
  'las-vegas': {
    key: 'las_vegas', label: 'Las Vegas', state: 'NV', full: 'Las Vegas, Nevada',
    tagline: 'Where Every Event is a Showstopper',
    description: "From casino nights and pool parties to corporate galas on the Strip, Eventism Las Vegas delivers the full Vegas experience. Showgirls, headliner DJs, and VIP bottle service — we do it all.",
    hero_gradient: 'from-purple-900 via-violet-900 to-slate-900',
    accent: 'purple',
    highlights: ['Strip & Off-Strip Venue Access', 'Casino Night Packages', 'Showgirl & Cirque Performers', 'Pool Party & Dayclub Production'],
    stats: { events: '200+', rating: '4.9', vendors: '18+' },
  },
  'los-angeles': {
    key: 'los_angeles', label: 'Los Angeles', state: 'CA', full: 'Los Angeles, California',
    tagline: 'Events as Bold as the City of Angels',
    description: 'Hollywood premieres, Malibu beach weddings, DTLA rooftop parties — Eventism LA brings award-winning production to every event in the City of Stars. Bilingual service available.',
    hero_gradient: 'from-amber-900 via-orange-900 to-slate-900',
    accent: 'amber',
    highlights: ['Hollywood Production Quality', 'Beachside & Rooftop Venues', 'Celebrity Chef Partners', 'Bilingual Event Crews'],
    stats: { events: '300+', rating: '4.8', vendors: '18+' },
  },
  charlotte: {
    key: 'charlotte', label: 'Charlotte', state: 'NC', full: 'Charlotte, North Carolina',
    tagline: 'Southern Charm, Modern Elegance',
    description: "Charlotte's premier event partner blending Southern hospitality with modern sophistication. From NoDa arts district launches to Uptown corporate galas, Eventism Charlotte has you covered.",
    hero_gradient: 'from-emerald-900 via-teal-900 to-slate-900',
    accent: 'emerald',
    highlights: ['Southern Hospitality Service', 'Carolina BBQ & Soul Food', 'Uptown & NoDa Venue Access', 'Charlotte Convention Support'],
    stats: { events: '150+', rating: '4.9', vendors: '18+' },
  }
};

export default function CityLandingPage() {
  const location = useLocation();
  const rawSlug = location.pathname.replace('/', '');
  // Accept both hyphen and underscore variants
  const citySlug = CITY_CONFIG[rawSlug]
    ? rawSlug
    : (CITY_CONFIG[rawSlug.replace('_', '-')]
        ? rawSlug.replace('_', '-')
        : rawSlug.replace('-', '_'));
  const navigate = useNavigate();
  const config = CITY_CONFIG[citySlug] || CITY_CONFIG[rawSlug.replace('_', '-')];

  const [services, setServices] = useState([]);
  const [leadForm, setLeadForm] = useState({ name: '', email: '', phone: '', event_type: '', guest_count: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!config) return;
    fetchServices();
  }, [citySlug]);

  const fetchServices = async () => {
    try {
      const res = await axios.get(`${API}/services/products?city=${config.key}&limit=50`);
      setServices(res.data?.products || []);
    } catch {}
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    if (!leadForm.name || !leadForm.email) { toast.error('Name and email required'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/leads`, {
        client_name: leadForm.name,
        client_email: leadForm.email,
        client_phone: leadForm.phone,
        event_type: leadForm.event_type || 'other',
        guest_count: parseInt(leadForm.guest_count) || 0,
        notes: leadForm.message,
        source: `city_landing_${config.key}`,
        city: config.key,
      });
      setSubmitted(true);
      toast.success("Got it — check your inbox for next steps!");
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(msg);
    } finally { setSubmitting(false); }
  };

  if (!config) {
    return <div className="min-h-screen pt-24 text-center"><h1 className="text-2xl font-bold">City not found</h1></div>;
  }

  const pageTitle = `${config.full} Event Services - Catering, DJs, Photo Booths | Eventism`;
  const pageDescription = `${config.full} event planning services. ${config.tagline}. Premium catering, DJs, photo booths, bartenders & more. Get a free quote today.`;

  const accentClasses = {
    indigo: { btn: 'bg-indigo-600 hover:bg-indigo-700', badge: 'bg-indigo-500', text: 'text-indigo-500', border: 'border-indigo-500', ring: 'focus:ring-indigo-500' },
    purple: { btn: 'bg-purple-600 hover:bg-purple-700', badge: 'bg-purple-500', text: 'text-purple-500', border: 'border-purple-500', ring: 'focus:ring-purple-500' },
    amber: { btn: 'bg-amber-600 hover:bg-amber-700', badge: 'bg-amber-500', text: 'text-amber-500', border: 'border-amber-500', ring: 'focus:ring-amber-500' },
    emerald: { btn: 'bg-emerald-600 hover:bg-emerald-700', badge: 'bg-emerald-500', text: 'text-emerald-500', border: 'border-emerald-500', ring: 'focus:ring-emerald-500' },
  }[config.accent];

  const catIcons = { food: ChefHat, drinks: Wine, entertainment: Music, staffing: Users, logistics: Truck, photography: Camera, decor: Sparkles };
  const categories = [...new Set(services.map(s => s.category))];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": `Eventism ${config.label}`,
    "description": config.description,
    "url": `https://eventism.com/${citySlug}`,
    "address": { "@type": "PostalAddress", "addressLocality": config.label, "addressRegion": config.state, "addressCountry": "US" },
    "areaServed": config.full,
    "aggregateRating": { "@type": "AggregateRating", "ratingValue": config.stats.rating, "reviewCount": "50" }
  };

  const seoKeywords = [
    `${config.label} event services`,
    `${config.label} event planning`,
    `${config.label} catering`,
    `${config.label} wedding planner`,
    `${config.label} corporate event services`,
    `${config.label} DJs`,
    `${config.label} party rentals`,
    `${config.label} photographers`,
    `${config.label} bartenders`,
    `${config.state} event planner`,
    `Eventism ${config.label}`,
    `event company in ${config.label}`,
    `${config.label} venue marketplace`,
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={pageTitle}
        description={pageDescription}
        keywords={seoKeywords}
        type="website"
        structuredData={[
          structuredData,
          organizationLD(),
          breadcrumbLD([
            { name: 'Home', url: '/' },
            { name: config.full, url: `/${citySlug}` },
          ]),
        ]}
      />

      {/* Hero */}
      <section className={`relative pt-20 pb-16 sm:pb-24 px-4 bg-gradient-to-br ${config.hero_gradient}`}>
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="h-5 w-5 text-white/70" />
              <span className="text-white/70 text-sm font-medium">{config.full}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-4">
              {config.tagline}
            </h1>
            <p className="text-lg text-white/80 mb-8 max-w-lg">{config.description}</p>

            {/* Stats */}
            <div className="flex gap-6 mb-8">
              <div><p className="text-2xl font-bold text-white">{config.stats.events}</p><p className="text-xs text-white/60">Events Produced</p></div>
              <div><p className="text-2xl font-bold text-white flex items-center gap-1"><Star className="h-5 w-5 text-yellow-400 fill-yellow-400" /> {config.stats.rating}</p><p className="text-xs text-white/60">Client Rating</p></div>
              <div><p className="text-2xl font-bold text-white">{config.stats.vendors}</p><p className="text-xs text-white/60">Services Available</p></div>
            </div>

            <div className="flex gap-3 flex-wrap">
              <Button size="lg" className={`${accentClasses.btn} text-white`} onClick={() => navigate('/plan')} data-testid="hero-cta">
                Plan Your Event <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10" onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}>
                Get a Free Quote
              </Button>
            </div>

            {/* Highlights */}
            <div className="mt-8 grid grid-cols-2 gap-2">
              {config.highlights.map((h, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-white/80">
                  <Check className="h-4 w-4 text-emerald-400 flex-shrink-0" /> {h}
                </div>
              ))}
            </div>
          </div>

          {/* Lead Capture Form */}
          <div id="contact-form" className="bg-card rounded-2xl p-6 sm:p-8 shadow-2xl border border-border" data-testid="lead-form">
            {submitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                  <Check className="h-8 w-8 text-emerald-500" />
                </div>
                <h3 className="text-xl font-bold mb-2">We Got Your Info!</h3>
                <p className="text-sm text-muted-foreground">Our {config.label} team will reach out within 24 hours. In the meantime, explore our services.</p>
                <Button className={`mt-4 ${accentClasses.btn}`} onClick={() => navigate('/plan')}>Start Planning Now</Button>
              </div>
            ) : (
              <>
                <h2 className="text-xl font-bold mb-1">Get a Free Quote</h2>
                <p className="text-sm text-muted-foreground mb-5">Tell us about your event and we'll get back to you within 24 hours.</p>
                <form onSubmit={handleLeadSubmit} className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <Input value={leadForm.name} onChange={e => setLeadForm(p => ({ ...p, name: e.target.value }))} placeholder="Your Name *" required data-testid="lead-name" />
                    <Input type="email" value={leadForm.email} onChange={e => setLeadForm(p => ({ ...p, email: e.target.value }))} placeholder="Email *" required data-testid="lead-email" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input value={leadForm.phone} onChange={e => setLeadForm(p => ({ ...p, phone: e.target.value }))} placeholder="Phone" data-testid="lead-phone" />
                    <Input type="number" value={leadForm.guest_count} onChange={e => setLeadForm(p => ({ ...p, guest_count: e.target.value }))} placeholder="Guest Count" data-testid="lead-guests" />
                  </div>
                  <select value={leadForm.event_type} onChange={e => setLeadForm(p => ({ ...p, event_type: e.target.value }))} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="lead-event-type">
                    <option value="">Event Type</option>
                    <option value="wedding">Wedding</option>
                    <option value="corporate">Corporate Event</option>
                    <option value="birthday">Birthday Party</option>
                    <option value="holiday">Holiday Party</option>
                    <option value="fundraiser">Fundraiser / Gala</option>
                    <option value="conference">Conference</option>
                    <option value="other">Other</option>
                  </select>
                  <textarea value={leadForm.message} onChange={e => setLeadForm(p => ({ ...p, message: e.target.value }))} placeholder="Tell us about your event..." rows={3} className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none" data-testid="lead-message" />
                  <Button type="submit" className={`w-full ${accentClasses.btn} text-white h-11`} disabled={submitting} data-testid="lead-submit">
                    {submitting ? 'Sending...' : 'Get My Free Quote'}
                  </Button>
                  <p className="text-[10px] text-muted-foreground text-center">No spam. No obligation. We respond within 24 hours.</p>
                </form>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Services by Category */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold mb-2">Our {config.label} Services</h2>
          <p className="text-muted-foreground">Everything you need for an unforgettable event</p>
        </div>

        {categories.map(cat => {
          const Icon = catIcons[cat] || Sparkles;
          const catServices = services.filter(s => s.category === cat);
          return (
            <div key={cat} className="mb-12">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2 capitalize">
                <Icon className={`h-5 w-5 ${accentClasses.text}`} /> {cat === 'food' ? 'Food & Catering' : cat === 'drinks' ? 'Bar & Drinks' : cat}
              </h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {catServices.map(s => {
                  // Build the canonical product detail URL: /:city/:category/:subCategory/:slug
                  const subSlug = (s.sub_category || s.subcategory || 'general').toLowerCase().replace(/[^a-z0-9]+/g, '-');
                  const productSlug = s.slug || (s.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-');
                  const productUrl = `/${(s.city || citySlug).toLowerCase()}/${s.category}/${subSlug}/${productSlug}`;
                  return (
                    <Card
                      key={s.service_id || s.product_id}
                      className="border-border hover:border-indigo-500/30 transition-all group cursor-pointer overflow-hidden"
                      onClick={() => navigate(productUrl)}
                      data-testid={`city-product-${s.service_id || s.product_id}`}
                    >
                      {s.image_url && (
                        <div className="aspect-[16/10] overflow-hidden bg-muted">
                          <img src={s.image_url} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                        </div>
                      )}
                      <CardContent className="p-4">
                        <h4 className="font-semibold text-sm mb-1">{s.name}</h4>
                        <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{s.short_description || s.description}</p>
                        <div className="flex items-center justify-between">
                          <span className={`text-sm font-bold ${accentClasses.text}`}>
                            ${s.base_price}{s.pricing_model === 'per_person' ? '/pp' : s.pricing_model === 'flat_rate' ? ' flat' : '/hr'}
                          </span>
                          {(s.option_groups || []).length > 0 && <Badge variant="outline" className="text-[9px]">Customizable</Badge>}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          );
        })}
      </section>

      {/* Bottom CTA */}
      <section className={`py-16 px-4 bg-gradient-to-br ${config.hero_gradient}`}>
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Create Something Amazing in {config.label}?</h2>
          <p className="text-white/70 mb-8">Join hundreds of happy clients who trust Eventism with their most important events.</p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100" onClick={() => navigate('/plan')}>
              Start Planning <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10" onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}>
              <Phone className="h-4 w-4 mr-2" /> Contact Us
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
